import { TooltipProvider } from '@neo/primitives/base/tootlip'
import { SidebarInset } from '@neo/primitives/sidebar'
import { Toaster } from '@neo/primitives/sonner'
import { SheetProvider } from '@shared/hooks/use-sheet'
import type { CommandEngineConfig } from 'cmdk-engine'
import { CommandEngineProvider, useCommandPalette } from 'cmdk-engine/react'
import { useMemo } from 'react'
import { Outlet, useNavigate } from 'router-v6'
import { AssistantPanel } from 'v2/pages/assistant/assistant-panel'
import { OnboardingGate } from 'v2/pages/onboarding'
import { AppHeader } from '../components/app-header'
import { AppSidebarWrapper } from '../components/app-sidebar-wrapper'
import { BlockingModal } from '../components/blocking-modal'
import { EffectsHandler } from '../components/effects-handler'
import { ErrorBoundary } from '../components/error-boundary'
import { GlobalCommandPalette } from '../components/global-command-palette/global-command-palette'
import { LocationEffectHandler } from '../components/location-effects-handler'
import { MobileBlockModal } from '../components/mobile-block-screen'
import { PersistentSonnerHandler } from '../components/persistent-sonner-handler'
import { PhoneHandler } from '../components/phone-handler'
import { Prefetcher } from '../components/prefetcher'
import { SidebarProviderWrapper } from '../components/sidebar-provider-wrapper'
import { PrimaryProvider } from '../hooks/use-primary'

// cmdk-engine's useCommandPalette() keeps isOpen/search/results as local state per call site
// (only the command registry is shared via context) — so it must be called exactly once and
// threaded down. The ⌘K dialog, the header search bar, and the sidebar search bar all drive
// this one instance.
const MainLayoutBody = () => {
  const commandPalette = useCommandPalette()

  return (
    <TooltipProvider>
      <SidebarProviderWrapper>
        <GlobalCommandPalette commandPalette={commandPalette} />
        <LocationEffectHandler />
        <Prefetcher />
        <EffectsHandler />
        <PhoneHandler />
        <AppSidebarWrapper />
        <SidebarInset className="flex-grow overflow-hidden flex flex-col h-screen">
          <AppHeader onSearchClick={commandPalette.open} />
          <div className="flex-1 overflow-hidden">
            <SheetProvider>
              <Outlet />
            </SheetProvider>
          </div>
        </SidebarInset>
        <AssistantPanel />
        <BlockingModal />
        <MobileBlockModal />
        <Toaster />
        <PersistentSonnerHandler />
      </SidebarProviderWrapper>
    </TooltipProvider>
  )
}

export const MainLayout = () => {
  const navigate = useNavigate()

  // Memoized so the cmdk engine (shared by every consumer) isn't rebuilt on every render —
  // an inline object literal here would give onSelect a fresh closure each time.
  const commandEngineConfig = useMemo<CommandEngineConfig>(
    () => ({
      onSelect: (item) => item.href && navigate(item.href),
      frecency: { storageKey: 'contacto-console-cmdk-frecency', showRecent: true },
      // Raw search text can contain contact names/numbers — never persist it (PII).
      searchHistory: { enabled: false },
    }),
    [navigate],
  )

  return (
    <ErrorBoundary>
      <PrimaryProvider>
        <CommandEngineProvider config={commandEngineConfig}>
          <OnboardingGate>
            <MainLayoutBody />
          </OnboardingGate>
        </CommandEngineProvider>
      </PrimaryProvider>
    </ErrorBoundary>
  )
}
